import { Observable } from 'rxjs';
import type { ExcelExportProgressService, ExcelExportProgressSlot } from './excel-export-progress.service';

/** Build config for row-data-export async CSV endpoints. */
export function createRowDataAsyncExportApi(
  apiBaseUrl: string,
  exportingLabel: string,
  defaultFileName: string,
): AsyncExportApiConfig {
  return { apiBaseUrl, exportingLabel, defaultFileName };
}

/** Standard async-export REST base, e.g. `${RawData_URL}/displayPlanogramExport`. */
export interface AsyncExportApiConfig {
  apiBaseUrl: string;
  /** Label during row export phase, e.g. "Exporting display planogram". */
  exportingLabel: string;
  /** Fallback filename when the server omits one. */
  defaultFileName: string;
}

/** Use the Excel-style name for queued CSV downloads (e.g. Fixture Share RawData.csv). */
export function csvFallbackNameFromExcelExport(excelFileName: string): string {
  return excelFileName.replace(/\.xlsx$/i, '.csv');
}

export interface AsyncExportToastHandlers {
  error: (message: string, title?: string, config?: Record<string, unknown>) => void;
  warning: (message: string) => void;
  info: (message: string) => void;
}

/**
 * One-call pattern used across survey / master-management export buttons
 * (Display Compliance, Display Planogram, Display Share, etc.).
 */
export interface CsvQueuedExportOptions {
  excelExportProgress: ExcelExportProgressService;
  api: AsyncExportApiConfig;
  params: Record<string, unknown>;
  /** Direct Excel/blob download when exportImageType === 2 or queue unavailable. */
  directDownload$: Observable<Blob>;
  directFileName: string;
  /** When true, skips queue and uses directDownload$ only. */
  withImageExport: boolean;
  /** Return false if validation failed (caller already showed an error). */
  validateWithImageExport?: () => boolean;
  embeddedImagesInfoMessage?: string;
  toast: AsyncExportToastHandlers;
  /** Optional transform of POST body (e.g. compliance adds columns). */
  buildStartBody?: (params: Record<string, unknown>) => Record<string, unknown>;
  onComplete?: () => void;
  onDirectDownloadStart?: () => void;
  onDirectDownloadEnd?: () => void;
  /** When true, 429 slot limit falls back to direct Excel download (legacy behaviour). */
  fallbackToDirectOnConcurrencyLimit?: boolean;
}

export interface AsyncExportErrorContext {
  err: unknown;
  toast: AsyncExportToastHandlers;
  directDownload$: Observable<Blob>;
  directFileName: string;
  excelExportProgress: ExcelExportProgressService;
  params: Record<string, unknown>;
  onFallbackDirect?: () => void;
  onAfterError?: () => void;
  fallbackToDirectOnConcurrencyLimit?: boolean;
}
